// Consistent Component Styling Constants
export const BUTTON_STYLES = {
  // Primary button styles
  primary: {
    variant: 'contained',
    color: 'primary',
    sx: {
      minWidth: 120,
      height: 40,
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      boxShadow: 2,
      '&:hover': {
        boxShadow: 4,
        transform: 'translateY(-1px)',
      },
      transition: 'all 0.2s ease-in-out',
    }
  },
  
  // Secondary button styles
  secondary: {
    variant: 'outlined',
    color: 'primary',
    sx: {
      minWidth: 120,
      height: 40,
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      borderWidth: 2,
      '&:hover': {
        borderWidth: 2,
        backgroundColor: 'primary.light',
        color: 'white',
        transform: 'translateY(-1px)',
      },
      transition: 'all 0.2s ease-in-out',
    }
  },
  
  // Small button styles
  small: {
    variant: 'contained',
    color: 'primary',
    size: 'small',
    sx: {
      minWidth: 80,
      height: 32,
      borderRadius: 1.5,
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.75rem',
    }
  },
  
  // Icon button styles
  icon: {
    sx: {
      padding: 1,
      borderRadius: 1,
      '&:hover': {
        backgroundColor: 'action.hover',
        transform: 'scale(1.05)',
      },
      transition: 'all 0.2s ease-in-out',
    }
  },
  
  // Action button styles (Edit, Delete, etc.)
  action: {
    size: 'small',
    sx: {
      padding: 0.5,
      borderRadius: 1,
      '&:hover': {
        transform: 'scale(1.1)',
      },
      transition: 'all 0.2s ease-in-out',
    }
  }
};

export const TEXT_FIELD_STYLES = {
  // Standard text field
  standard: {
    variant: 'outlined',
    fullWidth: true,
    margin: 'normal',
    sx: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
        },
      },
      '& .MuiInputLabel-root': {
        fontWeight: 500,
      },
    }
  },
  
  // Small text field
  small: {
    variant: 'outlined',
    fullWidth: true,
    margin: 'normal',
    size: 'small',
    sx: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 1.5,
        fontSize: '0.875rem',
      },
    }
  },
  
  // Search text field
  search: {
    variant: 'outlined',
    fullWidth: true,
    sx: {
      maxWidth: 400,
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        backgroundColor: 'background.paper',
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
        },
      },
    }
  }
};

export const MODAL_STYLES = {
  // Standard modal
  standard: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    maxHeight: '80vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
    overflowY: 'auto',
  },
  
  // Small modal
  small: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 400,
    maxHeight: '70vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 3,
    overflowY: 'auto',
  },
  
  // Large modal
  large: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: 800,
    maxHeight: '85vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
    overflowY: 'auto',
  }
};

export const FORM_STYLES = {
  // Standard form container
  container: {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      p: 3,
    }
  },
  
  // Form actions container
  actions: {
    sx: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 2,
      mt: 3,
      pt: 2,
      borderTop: '1px solid',
      borderColor: 'divider',
    }
  },
  
  // Form row (for side-by-side fields)
  row: {
    sx: {
      display: 'flex',
      gap: 2,
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    }
  },
  
  // Form field container
  field: {
    sx: {
      flex: 1,
      minWidth: 200,
    }
  }
};

export const CARD_STYLES = {
  // Standard card
  standard: {
    sx: {
      boxShadow: 2,
      borderRadius: 3,
      overflow: 'hidden',
      '&:hover': {
        boxShadow: 4,
      },
      transition: 'box-shadow 0.2s ease-in-out',
    }
  },
  
  // Header card
  header: {
    sx: {
      boxShadow: 2,
      borderRadius: 3,
      mb: 3,
    }
  },
  
  // Content card
  content: {
    sx: {
      boxShadow: 1,
      borderRadius: 2,
      mb: 2,
    }
  }
};

export const TABLE_STYLES = {
  // Table container
  container: {
    sx: {
      width: '100%',
      overflow: 'hidden',
      boxShadow: 2,
      borderRadius: 3,
    }
  },
  
  // Table header
  header: {
    sx: {
      backgroundColor: '#17479e',
      color: 'white',
      fontWeight: 600,
    }
  },
  
  // Table row
  row: {
    sx: {
      '&:nth-of-type(odd)': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
      transition: 'background-color 0.2s ease-in-out',
    }
  },
  
  // Table pagination
  pagination: {
    sx: {
      borderTop: '1px solid',
      borderColor: 'divider',
      '& .MuiTablePagination-toolbar': {
        paddingLeft: 2,
        paddingRight: 2,
      }
    }
  }
};

export const CHIP_STYLES = {
  // Status chips
  status: {
    size: 'small',
    variant: 'outlined',
    sx: {
      fontWeight: 500,
      borderRadius: 2,
    }
  },
  
  // Boolean chips
  boolean: {
    size: 'small',
    variant: 'outlined',
    sx: {
      fontWeight: 500,
      borderRadius: 2,
    }
  },
  
  // Category chips
  category: {
    size: 'small',
    variant: 'filled',
    sx: {
      fontWeight: 500,
      borderRadius: 2,
    }
  }
};

export const DIALOG_STYLES = {
  // Standard dialog
  standard: {
    maxWidth: 'sm',
    fullWidth: true,
    sx: {
      '& .MuiDialog-paper': {
        borderRadius: 3,
      }
    }
  },
  
  // Dialog title
  title: {
    sx: {
      pb: 1,
      fontWeight: 600,
    }
  },
  
  // Dialog content
  content: {
    sx: {
      fontSize: '1rem',
      lineHeight: 1.6,
    }
  },
  
  // Dialog actions
  actions: {
    sx: {
      p: 2,
      gap: 1,
    }
  }
};

// Common spacing values
export const SPACING = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 6,
};

// Common border radius values
export const BORDER_RADIUS = {
  small: 1,
  medium: 2,
  large: 3,
};

// Common shadow values
export const SHADOWS = {
  light: 1,
  medium: 2,
  heavy: 4,
};
